import {
  type Appointment,
  type ConfirmModalDefinition,
  type ConsentModalDefinition,
  type ConsentType,
  type Customer,
  type CustomerConsent,
  type CustomerModalDefinition,
  type CustomerProfileModalDefinition,
  type NewProjectModalDefinition,
  type SelectCoworkerAndTeamModalDefinition,
  type SelectDepartmentModalDefinition,
  type Task,
  getUserInfo,
  type hfb,
  useProfile,
} from "@repo/api";
import { Button, Checkbox, RadioButtonGroup } from "@repo/skapa";
import { t } from "@repo/translation";
import {
  assertUnreachable,
  isDefined,
  trackAnalytics,
  useTypesafeActions,
} from "@repo/utils";
import { useEffect } from "react";

import { FlexDiv } from "../../components/FlexDiv";
import { ModalSheet } from "../../components/ModalSheet";
import { SectionContainer } from "../../components/SectionContainer";
import {
  type ConsentRequirements,
  useSettings,
} from "../../settings/SettingsProvider";
import {
  type NewProjectState,
  type NewProjectStateActions,
  NewProjectStateDefault,
  newProjectStateActions,
  newProjectStateReducer,
} from "./NewProjectReducer";
import { NewProjectStep } from "./NewProjectStep";
import { SelectProjectStep } from "./SelectProjectStep";
import { CreateProjectButton } from "./components/CreateProjectButton";
import { TitleInput } from "./components/TitleInput";

interface NewProjectModalProps {
  isOpen: boolean;
  closeModal: () => void;
  onSelectProject: (projectId: number) => void;
  onProjectCreated: (projectId: number) => void;
  initialCustomerSearchValue?: string;
  associatedTask?: Task;
  associatedDropInAppointment?: Appointment;
  customerInfo: Customer; // mandatory
  departments?: hfb[];
  onBackView?: string;
  pushModal: (
    modalProps:
      | SelectCoworkerAndTeamModalDefinition
      | SelectDepartmentModalDefinition
      | ConsentModalDefinition
      | CustomerModalDefinition
      | ConfirmModalDefinition
      | CustomerProfileModalDefinition
      | NewProjectModalDefinition,
    reset?: boolean
  ) => void;
  filterCurrentProject?: number;
}

const getView = (
  state: NewProjectState,
  actions: NewProjectStateActions,
  pushModal: NewProjectModalProps["pushModal"],
  closeModal: () => void,
  onSelectProject: (projectId: number) => void,
  onProjectCreated: (projectId: number) => void,
  onBackView?: string,
  filterCurrentProject?: number
) => {
  switch (state.currentStep) {
    case "SELECT_PROJECT":
      return {
        title: t("action.select_project"),
        view: (
          <SelectProjectStep
            state={state}
            customer={state.customerInfo}
            onSelectProject={onSelectProject}
            pushModal={pushModal}
            filterCurrentProject={filterCurrentProject}
          />
        ),
        onBack:
          onBackView === "DISABLED"
            ? undefined
            : () => {
                pushModal(
                  {
                    type: "CUSTOMER_PROFILE",
                    pushModal,
                  },
                  true
                );
                trackAnalytics("back_from_create_new_project");
              },
        footer: (
          <>
            <Button
              type="primary"
              text={t("action.create_new_project")}
              onClick={() => {
                actions.setStep("NEW_PROJECT");
                actions.setDepartments([]);
                actions.setTitle(
                  t("label.new_sales_project_title", {
                    firstname: state.customerInfo?.firstName,
                    lastname: state.customerInfo?.lastName,
                    hfb: "",
                  })
                );
                trackAnalytics("create_new_project");
              }}
            />
          </>
        ),
      };
    case "NEW_PROJECT":
      return {
        title: t("action.create_new_project"),
        view: (
          <NewProjectStep
            state={state}
            actions={actions}
            pushModal={pushModal}
          />
        ),
        onBack: () => {
          actions.resetNewProject();
          actions.setStep("SELECT_PROJECT");
          trackAnalytics("back_from_create_new_project");
        },
        footer: (
          <CreateProjectButton
            state={state}
            closeModal={closeModal}
            onProjectCreated={onProjectCreated}
          />
        ),
      };

    case "TITLE":
      return {
        title: t("label.project_title"),
        view: <TitleInput state={state} actions={actions} />,
        onBack: () => {
          actions.setStep("NEW_PROJECT");
          trackAnalytics("back_from_new_project");
        },
        footer: (
          <Button
            type="primary"
            text={t("action.save_title")}
            onClick={() => {
              actions.setStep("NEW_PROJECT");
              trackAnalytics("save_title");
            }}
            disabled={!state.opportunityInfo?.title}
          />
        ),
      };

    case "CONTACT_METHOD":
      return {
        title: t("label.contact_method"),
        view: (
          <SectionContainer>
            <RadioButtonGroup
              subtle
              name={t("action.choose_contact_method")}
              list={[
                {
                  id: "EMAIL",
                  value: "EMAIL",
                  name: "contact-method",
                  label: t("label.email"),
                  defaultChecked: state.contactMethod === "EMAIL",
                  onChange: () => {
                    actions.setContactMethod("EMAIL");
                    trackAnalytics("contact_method");
                  },
                },
                {
                  id: "PHONE",
                  value: "PHONE",
                  name: "contact-method",
                  label: t("label.phone"),
                  defaultChecked: state.contactMethod === "PHONE",
                  onChange: () => {
                    actions.setContactMethod("PHONE");
                    trackAnalytics("contact_method");
                  },
                },
              ]}
            />

            <SectionContainer>
              <FlexDiv>
                <Checkbox
                  label={t("prompt.send_confirmation_message")}
                  id="confirmation"
                  value="confirmation"
                  checked={state.sendConfirmation}
                  onChange={(e) => {
                    actions.setConfirmation(e.target.checked);
                    trackAnalytics("send_confirmation_message");
                  }}
                  subtle
                />
              </FlexDiv>
            </SectionContainer>
          </SectionContainer>
        ),
        onBack: () => {
          actions.setStep("NEW_PROJECT");
        },
        footer: (
          <Button
            type="primary"
            text={t("action.save")}
            onClick={() => {
              actions.setStep("NEW_PROJECT");
              trackAnalytics("save_title");
            }}
            disabled={!state.contactMethod}
          />
        ),
      };
  }
  assertUnreachable(state.currentStep);
};

const getDefaultConsent = (
  requirements?: ConsentRequirements
): CustomerConsent[] | undefined => {
  if (requirements?.send_consent_approval === true) {
    return undefined;
  } else {
    const followupConsent = {
      consentType: "FOLLOWUP_CONSENT" as ConsentType,
      consentProvided:
        requirements?.default_followup_consent === "YES"
          ? true
          : requirements?.default_followup_consent === "NO"
            ? false
            : undefined,
    };
    const promotionalConsent = requirements?.promotional_consent
      ? {
          consentType: "PROMOTIONAL_CONSENT" as ConsentType,
          consentProvided: requirements?.promotional_consent
            ? false
            : undefined,
        }
      : undefined;
    return [followupConsent, promotionalConsent].filter(isDefined);
  }
};

export const NewProjectModal = ({
  isOpen,
  closeModal,
  onSelectProject,
  onProjectCreated,
  initialCustomerSearchValue,
  associatedTask,
  associatedDropInAppointment,
  customerInfo,
  departments,
  onBackView,
  pushModal,
  filterCurrentProject,
}: NewProjectModalProps) => {
  const { data: profile } = useProfile();
  const { consent_requirements } = useSettings();

  const defaultHfb =
    departments ?? (associatedTask?.hfb ? [associatedTask?.hfb] : []);
  const [newOpportunityState, actions] = useTypesafeActions<
    NewProjectState,
    NewProjectStateActions
  >(
    newProjectStateReducer,
    {
      ...NewProjectStateDefault,
      associatedTask,
      opportunityInfo: {
        ...NewProjectStateDefault.opportunityInfo,
        title: t("label.new_sales_project_title", {
          firstname: customerInfo?.firstName,
          lastname: customerInfo?.lastName,
          hfb: defaultHfb[0],
        }),
        hfb: defaultHfb,
      },
      customerSearchValue: initialCustomerSearchValue ?? "",
      currentStep: "SELECT_PROJECT",
      customerInfo,
      manualConsents: getDefaultConsent(consent_requirements),
      associatedDropInAppointment,
    },
    newProjectStateActions
  );

  const onClose = () => {
    closeModal();
    actions.setStep("SELECT_PROJECT");
    actions.setTitle("");
    actions.setCustomerSearchValue("");
  };

  useEffect(() => {
    if (profile) {
      actions.setAssignedCoworker(getUserInfo(profile));
      actions.setProfile(profile);
    }
  }, [profile, actions]);

  const { title, view, onBack, footer } = getView(
    newOpportunityState,
    actions,
    pushModal,
    onClose,
    onSelectProject,
    onProjectCreated,
    onBackView,
    filterCurrentProject
  );

  return (
    <ModalSheet
      title={title}
      visible={isOpen}
      onClose={onClose}
      onBack={onBack}
      footer={footer}
    >
      {view}
    </ModalSheet>
  );
};
